import useClevertapForSrp from '@/src/hooks/analytics/clevertap/srp'
import { StopOptionType } from '@/src/types/filterType'
import type { FilterJourneyIndex } from '@/src/types/srpJourneyIndex'
import { useTranslations } from 'next-intl'
import { useCallback, useMemo } from 'react'
import useUpdateSearchResultCache from '../updateSearchResultCache'
import useActiveFilterDataIndex from '../useActiveFilterDataIndex'
import useSearchResults from '../useSearchResults'

const useStopFilterManager = (filterJourneyIndex: FilterJourneyIndex = 0) => {
    const { filterData } = useSearchResults()
    const { filterDataIndex, isMulticity } = useActiveFilterDataIndex(filterJourneyIndex)
    const updateCache = useUpdateSearchResultCache()
    const t = useTranslations('common')
    const { triggerSrpEvent } = useClevertapForSrp()
    const title = t('stops', { s: 's' })
    const { NewStops } = filterData || {}
    const targetStops = NewStops?.[filterDataIndex]
    const { selectedValues: selectedStops, options, prices } = targetStops || {}

    const getStopLabelFromStopNumber = useCallback(
        (stops: number) => {
            return stops === 0 ? t('direct') : `${stops} ${stops > 1 ? t('stops', { s: 's' }) : t('stops', { s: '' })}`
        },
        [t]
    )

    const stopOptions: StopOptionType[] | undefined = useMemo(() => {
        return options?.map((option, index) => {
            return {
                id: option,
                label: getStopLabelFromStopNumber(Number(option)),
                price: prices?.[index] || 0,
                isSelected: !!selectedStops?.includes(option)
            }
        })
    }, [options, prices, selectedStops, getStopLabelFromStopNumber])

    //for multicity , we will check for all journeys if all of the journeys has non-stop option available
    const isNonstopFlightAvailable = useMemo(() => {
        if (isMulticity) {
            return Boolean(NewStops?.every(stops => stops.options?.length && stops.options.includes(0)))
        }
        return Boolean(stopOptions?.some(option => option.id === 0))
    }, [stopOptions, isMulticity, NewStops])

    const stopToggle = useCallback(
        (option: StopOptionType) => {
            const isRemoving = selectedStops?.includes(option.id)

            if (isRemoving) {
                updateCache(currentData => {
                    if (currentData?.filterData?.NewStops?.[filterDataIndex]) {
                        currentData.filterData.NewStops[filterDataIndex].selectedValues =
                            currentData.filterData.NewStops[filterDataIndex].selectedValues.filter(
                                s => s !== option.id
                            )
                    }
                    return currentData as NonNullable<typeof currentData>
                })
            } else {
                updateCache(currentData => {
                    if (currentData?.filterData?.NewStops?.[filterDataIndex]) {
                        currentData.filterData.NewStops[filterDataIndex].selectedValues.push(option.id)
                    }
                    return currentData as NonNullable<typeof currentData>
                })
            }

            const selectedValues = isRemoving
                ? selectedStops?.filter(s => s !== option.id) || []
                : [...(selectedStops || []), option.id]

            triggerSrpEvent('Air_UI_Action', {
                action_name: 'filter',
                action_type: 'stops',
                action_value: selectedValues.length > 0 ? selectedValues.join(',') : 'none'
            })
        },
        [selectedStops, updateCache, filterDataIndex, triggerSrpEvent]
    )

    const stopReset = useCallback(() => {
        updateCache(currentData => {
            if (currentData?.filterData?.NewStops?.[filterDataIndex]) {
                currentData.filterData.NewStops[filterDataIndex].selectedValues = []
            }
            return currentData as NonNullable<typeof currentData>
        })

        triggerSrpEvent('Air_UI_Action', {
            action_name: 'filter',
            action_type: 'reset',
            action_value: 'stops'
        })
    }, [updateCache, filterDataIndex, triggerSrpEvent])

    const nonStopChecked = useMemo(() => {
        if (isMulticity) {
            return Boolean(
                NewStops?.length &&
                NewStops.every(stops => stops.selectedValues?.length === 1 && stops.selectedValues.includes(0))
            )
        }
        return selectedStops?.length === 1 && selectedStops.includes(0)
    }, [isMulticity, NewStops, selectedStops])

    const nonStopToggle = useCallback(() => {
        if (isMulticity) {
            const enabling = !nonStopChecked
            updateCache(currentData => {
                currentData?.filterData?.NewStops?.forEach(stops => {
                    stops.selectedValues = enabling ? [0] : []
                })
                return currentData as NonNullable<typeof currentData>
            })

            triggerSrpEvent('Air_UI_Action', {
                action_name: 'filter',
                action_type: enabling ? 'stops' : 'reset',
                action_value: enabling ? '0' : 'stops'
            })
            return
        }

        if (nonStopChecked) {
            stopReset()
        } else {
            updateCache(currentData => {
                if (currentData?.filterData?.NewStops?.[filterDataIndex]) {
                    currentData.filterData.NewStops[filterDataIndex].selectedValues = [0]
                }
                return currentData as NonNullable<typeof currentData>
            })
        }
    }, [isMulticity, stopReset, nonStopChecked, updateCache, filterDataIndex, triggerSrpEvent])

    const shouldShowReset = useMemo(() => {
        return !!selectedStops?.length
    }, [selectedStops])

    const shouldShowResetForAllJourneys = useMemo(() => {
        if (!isMulticity) return false
        return !!NewStops?.some(stop => stop.selectedValues?.length)
    }, [isMulticity, NewStops])

    const stopResetAllJourneys = useCallback(() => {
        updateCache(currentData => {
            currentData?.filterData?.NewStops?.forEach(stop => {
                stop.selectedValues = []
            })
            return currentData as NonNullable<typeof currentData>
        })

        triggerSrpEvent('Air_UI_Action', {
            action_name: 'filter',
            action_type: 'reset',
            action_value: 'stops'
        })
    }, [updateCache, triggerSrpEvent])

    return {
        stopOptions,
        stopToggle,
        stopReset,
        stopResetAllJourneys,
        nonStopToggle,
        shouldShowReset,
        shouldShowResetForAllJourneys,
        nonStopChecked,
        title,
        isNonstopFlightAvailable
    }
}

export default useStopFilterManager
